import { useCallback, useEffect, useRef, useState } from 'react';

import { postTrackPing } from '../lib/track-location';
import { GeoError, type GeoPosition, getPositionPromise } from './utils/geolocation';

/**
 * - `idle`: this session is not tracked; render nothing.
 * - `needs-permission`: the browser is asking (or will ask). Shown instead of a spinner, because a
 *   prompt nobody answers never resolves and a spinner would spin forever.
 * - `send-failed`: we have a fix but api.core did not take it; the next ping retries.
 */
export type TrackingStatus =
	'idle' | 'needs-permission' | 'initializing' | 'active' | 'updating' | 'paused' | 'send-failed' | 'error' | 'permission-denied';

export interface LocationTrackingState {
	latitude: number | null;
	longitude: number | null;
	accuracy: number | null;
	error: string | null;
	loading: boolean;
	lastUpdated: string | null;
	status: TrackingStatus;
	retry: () => void;
}

export const TRACKING_INTERVAL = 600_000; // 10 minutes
/** Coming back to the tab re-pings, but not more than once a minute — tab-flicking is not movement. */
export const REFOCUS_THROTTLE = 60_000;

/**
 * A lookup that has not settled by now is abandoned. getCurrentPosition's own 15 s timeout only starts
 * once permission is granted, and some browsers never call back for a dismissed prompt — without this
 * the in-flight guard would stay set and every retry button would be dead until a reload. A prompt
 * answered later still lands: the permission `change` listener starts a fresh lookup.
 */
export const LOOKUP_WATCHDOG = 45_000;

/** States a retry keeps showing while it runs, so the screen does not flicker back to "Locating…". */
const STICKY_WHILE_RETRYING: TrackingStatus[] = ['needs-permission', 'permission-denied', 'error'];

const positionWithWatchdog = () =>
	new Promise<GeoPosition>((resolve, reject) => {
		const timer = setTimeout(
			() => reject(new GeoError('TIMEOUT', 'Your location could not be found. Try again in the open.')),
			LOOKUP_WATCHDOG
		);
		getPositionPromise().then(
			(p) => {
				clearTimeout(timer);
				resolve(p);
			},
			(e: unknown) => {
				clearTimeout(timer);
				reject(e instanceof Error ? e : new GeoError('POSITION_UNAVAILABLE', 'Unable to determine your location.'));
			}
		);
	});

type TrackingData = Omit<LocationTrackingState, 'retry'>;

const IDLE: TrackingData = {
	latitude: null,
	longitude: null,
	accuracy: null,
	error: null,
	loading: false,
	lastUpdated: null,
	status: 'idle',
};

const useLocationTracking = (isActive: boolean, entityCode?: string): LocationTrackingState => {
	const [state, setState] = useState<TrackingData>(IDLE);

	// Kept in a ref so the interval/visibility callbacks read the latest posting code without
	// re-subscribing. A code that resolves shortly after mount still attaches to the next ping.
	const entityCodeRef = useRef(entityCode);
	entityCodeRef.current = entityCode;

	const activeRef = useRef(isActive);
	activeRef.current = isActive;

	// One request at a time: the interval, a refocus and a manual retry can otherwise overlap, and a
	// slow early fix would overwrite a newer one.
	const inflightRef = useRef(false);
	const lastAttemptRef = useRef(0);

	const fetchAndPost = useCallback(async () => {
		if (inflightRef.current || !activeRef.current) return;
		inflightRef.current = true;
		lastAttemptRef.current = Date.now();

		setState((prev) => ({
			...prev,
			loading: true,
			status: prev.latitude !== null ? 'updating' : STICKY_WHILE_RETRYING.includes(prev.status) ? prev.status : 'initializing',
		}));

		try {
			const position = await positionWithWatchdog();
			if (!activeRef.current) return;
			const timestamp = new Date().toISOString();

			setState({
				latitude: position.latitude,
				longitude: position.longitude,
				accuracy: position.accuracy,
				error: null,
				loading: false,
				lastUpdated: timestamp,
				status: 'active',
			});

			const sent = await postTrackPing({
				latitude: position.latitude,
				longitude: position.longitude,
				accuracy: position.accuracy,
				timestamp,
				entityCode: entityCodeRef.current,
			});
			if (!sent && activeRef.current) {
				setState((prev) => (prev.lastUpdated === timestamp ? { ...prev, status: 'send-failed' } : prev));
			}
		} catch (err) {
			if (!activeRef.current) return;
			const geoError = err as GeoError;
			setState((prev) => ({
				...prev,
				loading: false,
				error: geoError.message,
				status: geoError.code === 'PERMISSION_DENIED' ? 'permission-denied' : 'error',
			}));
		} finally {
			inflightRef.current = false;
		}
	}, []);

	const retry = useCallback(() => {
		void fetchAndPost();
	}, [fetchAndPost]);

	// Start: read the permission first where the browser can say, so a pending prompt is shown as
	// "Allow location" and a blocked one as "Location off" — not as an endless "Locating…".
	useEffect(() => {
		if (!isActive) {
			setState(IDLE);
			return undefined;
		}

		let permission: PermissionStatus | null = null;
		let cancelled = false;

		const onPermissionChange = () => {
			if (!permission) return;
			if (permission.state === 'denied') {
				setState((prev) => ({ ...prev, loading: false, status: 'permission-denied' }));
			} else if (permission.state === 'granted') {
				void fetchAndPost();
			}
		};

		const start = async () => {
			try {
				permission = (await navigator.permissions?.query({ name: 'geolocation' })) ?? null;
			} catch {
				permission = null;
			}
			if (cancelled) return;

			permission?.addEventListener('change', onPermissionChange);

			if (permission?.state === 'denied') {
				setState({ ...IDLE, status: 'permission-denied' });
				return;
			}
			setState({ ...IDLE, loading: true, status: permission?.state === 'prompt' ? 'needs-permission' : 'initializing' });
			void fetchAndPost();
		};

		void start();

		const interval = setInterval(() => {
			void fetchAndPost();
		}, TRACKING_INTERVAL);

		return () => {
			cancelled = true;
			clearInterval(interval);
			permission?.removeEventListener('change', onPermissionChange);
		};
	}, [isActive, fetchAndPost]);

	useEffect(() => {
		if (!isActive) return undefined;

		const handleVisibilityChange = () => {
			if (document.visibilityState === 'hidden') {
				setState((prev) => ({ ...prev, status: prev.status === 'active' ? 'paused' : prev.status }));
				return;
			}
			if (Date.now() - lastAttemptRef.current >= REFOCUS_THROTTLE) {
				void fetchAndPost();
			} else {
				setState((prev) => ({ ...prev, status: prev.status === 'paused' ? 'active' : prev.status }));
			}
		};

		document.addEventListener('visibilitychange', handleVisibilityChange);
		return () => {
			document.removeEventListener('visibilitychange', handleVisibilityChange);
		};
	}, [isActive, fetchAndPost]);

	return {
		...state,
		retry,
	};
};

export default useLocationTracking;
